import React, { useState } from 'react';
import { BannerSize } from '../types';
import { X, Save, Ruler, Grid, Square, Minus, RotateCcw } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { v4 as uuidv4 } from 'uuid';

interface AddBannerSizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddBannerSize: (bannerSize: BannerSize) => void;
  existingSizes: BannerSize[];
}

export const AddBannerSizeModal: React.FC<AddBannerSizeModalProps> = ({
  isOpen,
  onClose,
  onAddBannerSize,
  existingSizes
}) => {
  const [formData, setFormData] = useState({
    name: '',
    width: 300,
    height: 250,
    category: 'horizontal' as 'square' | 'horizontal' | 'vertical'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { showSuccess, showError, showWarning } = useToast();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showError('Validation Error', 'Please enter a banner size name');
      return;
    }

    if (formData.width < 50 || formData.width > 5000) {
      showError('Validation Error', 'Width must be between 50 and 5000 pixels');
      return;
    }

    if (formData.height < 50 || formData.height > 5000) {
      showError('Validation Error', 'Height must be between 50 and 5000 pixels');
      return;
    }

    // Check for duplicate dimensions
    const duplicateSize = existingSizes.find(size => 
      size.width === formData.width && size.height === formData.height
    );

    if (duplicateSize) {
      showWarning('Duplicate Size', `A banner size with dimensions ${formData.width}×${formData.height} already exists (${duplicateSize.name})`);
      return;
    }

    // Check for duplicate name
    const duplicateName = existingSizes.find(size => 
      size.name.toLowerCase() === formData.name.trim().toLowerCase()
    );

    if (duplicateName) {
      showError('Duplicate Name', 'A banner size with this name already exists');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      const newBannerSize: BannerSize = {
        id: uuidv4(),
        name: formData.name.trim(),
        width: formData.width,
        height: formData.height,
        category: formData.category,
        elements: [],
        lastModified: new Date()
      };

      onAddBannerSize(newBannerSize);
      showSuccess('Banner Size Added', `${newBannerSize.name} (${newBannerSize.width}×${newBannerSize.height}) has been added successfully`);
      
      // Reset form
      setFormData({
        name: '',
        width: 300,
        height: 250,
        category: 'horizontal'
      });
      
      onClose();
    } catch (error) {
      showError('Error', 'Failed to add banner size. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDimensionChange = (field: 'width' | 'height', value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => {
      const newData = { ...prev, [field]: numValue };
      
      // Auto-detect category based on dimensions
      const aspectRatio = newData.width / newData.height;
      let category: 'square' | 'horizontal' | 'vertical';
      
      if (Math.abs(aspectRatio - 1) < 0.1) {
        category = 'square';
      } else if (aspectRatio > 1) {
        category = 'horizontal';
      } else {
        category = 'vertical';
      }
      
      return { ...newData, category };
    });
  };

  const swapDimensions = () => {
    setFormData(prev => ({
      ...prev,
      width: prev.height,
      height: prev.width,
      category: prev.category === 'horizontal' ? 'vertical' : 
                prev.category === 'vertical' ? 'horizontal' : 'square'
    }));
  };

  const setPresetSize = (width: number, height: number, name: string) => {
    setFormData(prev => ({
      ...prev,
      width,
      height,
      name: prev.name || name,
      category: width === height ? 'square' : width > height ? 'horizontal' : 'vertical'
    }));
  };

  const commonSizes = [
    { width: 728, height: 90, name: 'Leaderboard' },
    { width: 300, height: 250, name: 'Medium Rectangle' },
    { width: 320, height: 50, name: 'Mobile Banner' },
    { width: 160, height: 600, name: 'Wide Skyscraper' },
    { width: 970, height: 250, name: 'Billboard' },
    { width: 300, height: 600, name: 'Half Page' },
    { width: 320, height: 100, name: 'Large Mobile Banner' },
    { width: 468, height: 60, name: 'Banner' }
  ];

  const categoryOptions = [
    { value: 'horizontal', label: 'Horizontal', icon: Minus, description: 'Wider than tall (landscape)' },
    { value: 'square', label: 'Square', icon: Square, description: 'Equal width and height' },
    { value: 'vertical', label: 'Vertical', icon: Grid, description: 'Taller than wide (portrait)' }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'horizontal': return 'border-blue-500 bg-blue-50';
      case 'square': return 'border-green-500 bg-green-50';
      case 'vertical': return 'border-purple-500 bg-purple-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const getAspectRatio = () => {
    if (formData.height === 0) return 'Invalid';
    const ratio = formData.width / formData.height;
    return `${ratio.toFixed(2)}:1`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Ruler className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add Custom Banner Size</h2>
              <p className="text-sm text-gray-600">Create a custom banner size for specific needs</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Banner Size Name */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Banner Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Banner Size Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                placeholder="e.g., Custom Header, Special Sidebar, Mobile Footer"
                required
              />
            </div>
          </div>

          {/* Dimensions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dimensions</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Width (px) *
                </label>
                <input
                  type="number"
                  min="50"
                  max="5000"
                  value={formData.width}
                  onChange={(e) => handleDimensionChange('width', e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  required
                />
              </div>

              <div className="flex items-end justify-center">
                <button
                  type="button"
                  onClick={swapDimensions}
                  disabled={isSubmitting}
                  className="p-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Swap Width and Height"
                >
                  <RotateCcw size={20} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height (px) *
                </label>
                <input
                  type="number"
                  min="50"
                  max="5000"
                  value={formData.height}
                  onChange={(e) => handleDimensionChange('height', e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  required
                />
              </div>
            </div>

            {/* Dimension Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Dimensions:</span>
                  <p className="text-gray-900">{formData.width} × {formData.height}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Aspect Ratio:</span>
                  <p className="text-gray-900">{getAspectRatio()}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Category:</span>
                  <p className="text-gray-900 capitalize">{formData.category}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Total Pixels:</span>
                  <p className="text-gray-900">{(formData.width * formData.height).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Common Sizes */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Common Banner Sizes</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {commonSizes.map((size, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setPresetSize(size.width, size.height, size.name)}
                  disabled={isSubmitting}
                  className="p-3 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <div className="font-medium text-gray-900 text-sm">{size.name}</div>
                  <div className="text-xs text-gray-600">{size.width} × {size.height}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Category</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {categoryOptions.map((category) => (
                <label
                  key={category.value}
                  className={`flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                    formData.category === category.value ? getCategoryColor(category.value) : ''
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={category.value}
                    checked={formData.category === category.value}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    disabled={isSubmitting}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <category.icon size={20} className={formData.category === category.value ? 'text-blue-600' : 'text-gray-500'} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{category.label}</div>
                    <div className="text-sm text-gray-600">{category.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Visual Preview */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 flex items-center justify-center">
              <div className="text-center">
                <div
                  className="bg-white border-2 border-dashed border-gray-300 mx-auto mb-3 flex items-center justify-center"
                  style={{
                    width: Math.min(formData.width / 2, 200),
                    height: Math.min(formData.height / 2, 150),
                    minWidth: 60,
                    minHeight: 40
                  }}
                >
                  <span className="text-xs text-gray-500 font-medium">
                    {formData.width} × {formData.height}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Preview at {Math.round((Math.min(formData.width / 2, 200) / formData.width) * 100)}% scale
                </p>
              </div>
            </div>
          </div>

          {/* Validation Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Size Guidelines</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• Minimum size: 50×50 pixels</p>
              <p>• Maximum size: 5000×5000 pixels</p>
              <p>• Consider standard web banner sizes for better compatibility</p>
              <p>• Larger banners may take longer to load and export</p>
              <p>• The banner will be empty initially - you can start designing immediately</p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Adding banner size...</span>
              </div>
            ) : (
              'Custom banner size will be added to this banner set'
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.name.trim() || formData.width < 50 || formData.height < 50}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} />
              <span>{isSubmitting ? 'Adding...' : 'Add Banner Size'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};